// src/components/GanttChart.tsx
import React, { useEffect, useState, useRef } from "react";
import { Gantt, ViewMode } from "gantt-task-react";
import JCPasswordModal from "./JCPasswordModal";
import { Toast } from "primereact/toast";
import CustomTaskListHeader from "./CustomHeader";
import CustomTaskListTable from "./CustomTaskListTable";
import { Workpack, CustomTask } from "../helper";
import { updateTaskReorder,
  updateTaskWithProgress,
  verifyProgressPassword,
} from "../Api";
import { ViewSwitcher } from "./view-switcher";
interface GanttChartProps {
  taskdisplay: CustomTask[];
  view: ViewMode;
  workpack: Workpack | null;
  setView: React.Dispatch<React.SetStateAction<ViewMode>>;
  isganttLoading: boolean;
  openSign: () => void;
  openTemplate: () => void;
  handleTaskChange: (task: any) => void;
  handleTaskDelete: (task: any) => boolean;
  handleProgressChange: (task: any) => void;
  handleDblClick: (task: any) => void;
  handleClick: (task: any) => void;
  handleSelect: (task: any, sel: boolean) => void;
  onExpanderClick: (task: any) => void;
  handleAddTask: (task: any) => void;
  setDate: (task: any) => void;
  isTaskUpdating: boolean;
  onReorderComplete?: () => void;
  setIsTaskUpdating: (updating: boolean) => void;
}

const GanttChart: React.FC<GanttChartProps> = ({
  taskdisplay,
  view,
  isganttLoading,
  workpack,
  handleTaskChange,
  handleTaskDelete,
  handleProgressChange,
  handleDblClick,
  handleClick,
  handleSelect,
  onExpanderClick,
  handleAddTask,
  setDate,
  isTaskUpdating,
  setView,
  openSign,
  openTemplate,
  onReorderComplete,
  setIsTaskUpdating,
}) => {
  const toast = useRef<Toast>(null);
  const lastLockedToastTs = useRef<number>(0);
  // --------------------
  // Normalizer (used immediately)
  // --------------------
  const normalizeTask = (t: any): CustomTask | null => {
    if (!t || typeof t !== "object") return null;

    const safeDate = (val: any, fallback?: Date) => {
      if (val === null || val === undefined) return fallback ?? new Date();
      const d = new Date(val);
      return isNaN(d.getTime()) ? fallback ?? new Date() : d;
    };

    // Ensure start date exists and is valid
    const start = safeDate(t.start);
    let end = safeDate(t.end, start);

    // Ensure end is not before start
    if (end.getTime() < start.getTime()) {
      end = new Date(start);
    }

    return {
      ...t,
      start,
      end,
      progress: t.progress ?? 0,
      is_locked: t.is_locked ?? false,
      hideChildren: t.hideChildren ?? false,
      progressHandleVisible:
        typeof t.progressHandleVisible === "boolean"
          ? t.progressHandleVisible
          : !t.is_locked,
    } as CustomTask;
  };
  // --------------------
  // Initialize tasks state from normalized incoming prop
  // --------------------
  const initialNormalized = (taskdisplay || [])
    .map(normalizeTask)
    .filter(Boolean) as CustomTask[];
  const [tasks, setTasks] = useState<CustomTask[]>(initialNormalized);
  // ----------------------------
  // JC Lock Modal State - MOVED TO TOP LEVEL
  // ---------------------------
  const [jcModalVisible, setJcModalVisible] = React.useState(false);
  const [jcTaskId, setJcTaskId] = useState<string | number | null>(null);
  const [jcIsLocked, setJcIsLocked] = useState(false);
  const [jcPasswordError, setJcPasswordError] = useState("");
  // When prop changes, normalize immediately (prevents first-render crash)
  useEffect(() => {
    const normalized = (taskdisplay || [])
      .map(normalizeTask)
      .filter(Boolean) as CustomTask[];
    setTasks(normalized);
  }, [taskdisplay]);
  // --------------------
  // SANITIZER BEFORE SENDING TO GANTT
  // --------------------
  const safeTasks: CustomTask[] = tasks.map(normalizeTask).filter(
    (t) =>
      t &&
      t.start instanceof Date &&
      t.end instanceof Date &&
      !isNaN(t.start.getTime()) &&
      !isNaN(t.end.getTime()) &&
      t.start.getTime() <= t.end.getTime() // Additional validation: start should be before or equal to end
  ) as CustomTask[];
  // Add safety check: if no valid tasks, return empty state
  if (safeTasks.length === 0) {
    return null;
  }
  if (isganttLoading) return <>Loading…</>;
  // Called from CustomTaskListTable
  const handleJCLockClick = (jcId: string | number, isLocked: boolean) => {
    setJcTaskId(jcId);
    setJcIsLocked(isLocked);
    setJcPasswordError("");
    setJcModalVisible(true);
  };
  // --------------------
  // SAFE JC LOCK — uses normalized snapshot to avoid corruption
  // --------------------
  const toggleJCLocks = async (jcId: string | number, lock: boolean) => {
    const jcIdStr = jcId?.toString();

    // Normalize current snapshot (don't mutate original tasks objects directly)
    const normalizedSnapshot = (tasks || [])
      .map(normalizeTask)
      .filter(Boolean) as CustomTask[];

    // 1) Update UI safely using the normalized snapshot
    const updatedTasks = normalizedSnapshot.map((t) =>
      t.project?.toString() === jcIdStr
        ? {
            ...t,
            is_locked: lock,
            progressHandleVisible: !lock,
            start: new Date(t.start),
            end: new Date(t.end),
          }
        : t
    );

    setTasks(updatedTasks);

    // 2) Update backend for children (use normalizedSnapshot to determine children list)
    const children = normalizedSnapshot.filter(
      (t) => t.project?.toString() === jcIdStr
    );

    try {
      await Promise.all(
        children.map((t) =>
          updateTaskWithProgress({
            id: t.id,
            parent: t.project,
            is_locked: lock,
            progress: t.progress ?? 0,
          })
        )
      );
    } catch (err) {
      console.error("Error toggling JC locks:", err);
      // on error, revert by re-applying normalizedSnapshot (safe)
      setTasks(normalizedSnapshot);
      throw err;
    }
  };
  const handleJCModalSubmit = async (password: string) => {
    if (jcTaskId === null) {
      setJcPasswordError("No Job Card selected");
      return;
    }

    try {
      const isValid = await verifyProgressPassword(password);
      if (!isValid) {
        setJcPasswordError("Incorrect password");
        return;
      }
    } catch (err) {
      console.error("Error verifying progress password", err);
      setJcPasswordError("Unable to verify password — please try again");
      return;
    }

    try {
      // Close password dialog immediately, then show global loader
      setJcModalVisible(false);
      // Show global loading overlay while toggling JC locks
      setIsTaskUpdating(true);
      await toggleJCLocks(jcTaskId, !jcIsLocked);
    } catch (err) {
      setJcPasswordError("Failed to toggle JC locks — try again");
    } finally {
      // Hide global loading overlay once JC lock operation completes
      setIsTaskUpdating(false);
    }
  };
  // --------------------
  // HANDLE REORDER - MOVED INSIDE COMPONENT
  // --------------------

  const arrangeTasks = (
    tasks: CustomTask[],
    sourceIndex: number,
    destIndex: number
  ) => {
    const updated = [...tasks];

    const [movedTask] = updated.splice(sourceIndex, 1);
    updated.splice(destIndex, 0, movedTask);

    // Update op_no only for same project tasks
    const sameProjectTasks = updated.filter(
      (t) => t.project === movedTask.project
    );

    sameProjectTasks.forEach((task, index) => {
      task.op_no = (index + 1) * 10;
    });

    return {
      updatedTasks: updated,
      sameProjectTasks,
    };
  };
  const sendReorderedTasks = async (tasks: CustomTask[]) => {
    
    if (!tasks || tasks.length === 0) {
      console.warn("⚠️ No tasks to reorder");
      return;
    }
    try {
      const results = await Promise.allSettled(
        tasks.map(async (task) => {
          return updateTaskReorder({ id: task.id as string, op_no: task.op_no });
        })
      );
      const failed = results.filter(result => result.status === 'rejected');
      if (failed.length > 0) {
        // Show user-friendly error notification
        toast.current?.show({
          severity: "error",
          summary: "Reorder Failed",
          detail: `Failed to update ${failed.length} task(s). Please try again.`,
          life: 5000,
        });
      }
    } catch (err) {
      throw err;
    }
  };

  const handleReorder = async (sourceIndex: number, destIndex: number) => {
    try {
      // Show global loading overlay during reorder
      setIsTaskUpdating(true);
      const { updatedTasks, sameProjectTasks } = arrangeTasks(
        tasks,
        sourceIndex,
        destIndex
      );
      setTasks(updatedTasks);
      await sendReorderedTasks(sameProjectTasks);
      
      if (onReorderComplete) {
        onReorderComplete();
      }
      
      return updatedTasks;
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Reorder Error",
        detail: "Failed to reorder tasks. Please try again.",
        life: 5000,
      });
      return tasks;
    } finally {
      // Hide global loading overlay once reorder finishes
      setIsTaskUpdating(false);
    }
  };
  // --------------------
  // Progress change guard: prevent editing when task is locked
  // Signature matches gantt-task-react: (task, children)
  // --------------------
  const handleProgressChangeSafe = (task: any, children: any[]) => {
    if (task.is_locked) {
      const now = Date.now();
      if (now - lastLockedToastTs.current > 500) {
        lastLockedToastTs.current = now;
        toast.current?.show({
          severity: "error",
          summary: "Locked",
          detail: "The task is locked",
          life: 3000,
        });
      }
      // returning false asks gantt-task-react to revert the change
      return false;
    }
    try {
      handleProgressChange(task);
    } catch (e) {
    }
    return true;
  };
  return (
    <>
      <Toast ref={toast} />
      <ViewSwitcher
        onViewModeChange={setView}
        workpack={workpack}
        openModal={openSign}
        openTemplate={openTemplate}
        loading={isTaskUpdating}
      />
      <Gantt
        tasks={safeTasks}
        viewMode={view}
        onDateChange={handleTaskChange}
        onDelete={handleTaskDelete}
        onProgressChange={handleProgressChangeSafe}
        onDoubleClick={handleDblClick}
        onClick={handleClick}
        onSelect={handleSelect}
        onExpanderClick={onExpanderClick}
        arrowColor="transparent"
        rowHeight={62}
        ganttHeight={630}
        TaskListHeader={CustomTaskListHeader}
        TaskListTable={(props) => (
          <CustomTaskListTable
            {...props}
            workpack={workpack}
            setDate={setDate}
            handleAddTask={handleAddTask}
            onReorder={handleReorder}
            onJCLockClick={handleJCLockClick}
          />
        )}
      />
      <JCPasswordModal
        show={jcModalVisible}
        onHide={() => setJcModalVisible(false)}
        onSubmit={handleJCModalSubmit}
        error={jcPasswordError}
        isLocked={jcIsLocked}
      />
    </>
  );
};

export default GanttChart;